export default class StringUtils {
	static #isAppPageUrl(url) {
		if (!url?.trim() || !/^https?:\/\//i.test(url)) {
			return false;
		}

		try {
			const parsed = new URL(url, window.location.origin);
			if (parsed.origin !== window.location.origin) {
				return false;
			}

			return /\/app\//i.test(parsed.pathname);
		} catch {
			return false;
		}
	}

	static snakeToCamel(str) {
		return str.toLowerCase().replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('-', '').replace('_', ''));
	}

	static extractVideoCode(url, type) {
		if (!url) return { code: '', type: type };

		const patterns = {
			youtube: [
				/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
				/youtube\.com\/embed\/([^&\n?#]+)/,
				/youtube\.com\/shorts\/([^&\n?#]+)/,
				/youtube\.com\/live\/([^&\n?#]+)/,
			],
			vimeo: [/vimeo\.com\/([0-9]+)/, /player\.vimeo\.com\/video\/([0-9]+)/],
			facebookVideo: [
				/facebook\.com\/.*?\/videos\/([0-9]+)/,
				/facebook\.com\/watch\/(?:live\/)?\?.*?v=([0-9]+)/,
				/facebook\.com\/watch\/\?v=([0-9]+)/,
				/facebook\.com\/plugins\/video\.php\?href=[^"'&\s]*?[?&]v%3D([0-9]+)/i,
				/facebook\.com\/plugins\/video\.php\?href=[^"'&\s]*?videos(?:%2F|\/)([0-9]+)/i,
			],
			twitchVideo: [/twitch\.tv\/videos\/([0-9]+)/, /player\.twitch\.tv\/\?.*?video=([0-9]+)/],
			twitchChannel: [/twitch\.tv\/([a-zA-Z0-9_]+)/, /player\.twitch\.tv\/\?.*?channel=([a-zA-Z0-9_]+)/],
			meride: [/meride-tv\.akamaized\.net\/meride\/[^\/]+\/([a-zA-Z0-9_-]+)/, /data-embed=["']([a-zA-Z0-9_-]+)["']/],
			streamup: [/streamup\.eu\/.+\/([a-zA-Z0-9]+)(?:[\/?#]|$)/i],
		};

		// Se è un URL, proviamo a determinare automaticamente il tipo
		if (url.includes('://') || url.startsWith('www.')) {
			for (const [videoType, typePatterns] of Object.entries(patterns)) {
				for (const pattern of typePatterns) {
					const match = url.match(pattern);
					if (match && match[1]) {
						return {
							code: match[1],
							type: videoType,
							aspect: url.includes('youtube.com/shorts/') ? 'short' : url.includes('youtube.com/live/') ? 'live' : 'video',
						};
					}
				}
			}
		}

		// Se è un video caricato direttamente o non è un URL, mantieni il tipo originale
		if (type === 'video') {
			if (StringUtils.#isAppPageUrl(url)) {
				return { code: '', type: 'video', aspect: 'video' };
			}

			return { code: url, type: 'video', aspect: 'video' };
		}

		// Verifica se l'input è già un codice video del tipo specificato
		const typePatterns = patterns[type];
		if (!typePatterns) return { code: '', type: type, aspect: 'video' };

		// Verifica se corrisponde ai pattern del tipo specificato
		for (const pattern of typePatterns) {
			const match = url.match(pattern);
			if (match && match[1]) {
				return { code: match[1], type: type, aspect: 'video' };
			}
		}

		// Verifica se l'input è già un codice valido per il tipo specificato
		if (type === 'youtube' && /^[\w-]{11}$/.test(url))
			return { code: url, type: type, aspect: url.includes('shorts') ? 'short' : url.includes('live') ? 'live' : 'video' };
		if (type === 'youtube' && /iframe/.test(url))
			return {
				code: url.match(/src="([^"]+)"/)[1].match(/[\w-]{11}/)[0],
				type: type,
				aspect: url.includes('shorts') ? 'short' : url.includes('live') ? 'live' : 'video',
			};
		if (type === 'vimeo' && /^[0-9]+$/.test(url)) return { code: url, type: type, aspect: 'video' };
		if (type === 'facebookVideo' && /^[0-9]+$/.test(url)) return { code: url, type: type, aspect: 'video' };
		if (type === 'facebookVideo' && /iframe/i.test(url)) {
			const hrefMatch = url.match(/plugins\/video\.php\?href=([^"&\s]+)/i);
			if (hrefMatch?.[1]) {
				const decoded = decodeURIComponent(hrefMatch[1]);
				const nested = StringUtils.extractVideoCode(decoded, 'facebookVideo');
				if (nested.code) {
					return nested;
				}
			}
		}
		if (type === 'twitchVideo' && /^[0-9]+$/.test(url)) return { code: url, type: type, aspect: 'video' };
		if (type === 'twitchChannel' && /^[a-zA-Z0-9_]+$/.test(url)) return { code: url, type: type, aspect: 'video' };
		if (type === 'meride' && /^[a-zA-Z0-9_-]+$/.test(url)) return { code: url, type: type, aspect: 'video' };
		if (type === 'streamup' && /^[a-zA-Z0-9]+$/.test(url)) return { code: url, type: type, aspect: 'video' };

		return { code: '', type: type, aspect: 'video' };
	}

	static extractAudioCode(url, type) {
		if (!url) return { code: '', type: type };

		const patterns = {
			spreakerplaylist: [/spreaker\.com\/show\/([0-9]+)/, /widget\.spreaker\.com\/player\?show_id=([0-9]+)/],
			spreaker: [/spreaker\.com\/episode\/([0-9]+)/, /widget\.spreaker\.com\/player\?episode_id=([0-9]+)/],
		};

		if (url.includes('://') || url.startsWith('www.')) {
			for (const [audioType, typePatterns] of Object.entries(patterns)) {
				for (const pattern of typePatterns) {
					const match = url.match(pattern);
					if (match && match[1]) {
						return { code: match[1], type: audioType };
					}
				}
			}
		}

		const typePatterns = patterns[type];
		if (!typePatterns) return { code: '', type: type };

		for (const pattern of typePatterns) {
			const match = url.match(pattern);
			if (match && match[1]) {
				return { code: match[1], type: type };
			}
		}

		if (type === 'spreakerplaylist' && /^[0-9]+$/.test(url)) return { code: url, type: type };
		if (type === 'spreakerplaylist' && /--[0-9]+$/.test(url)) return { code: url.match(/--([0-9]+)$/)[1], type: type };
		if (type === 'spreaker' && /^[0-9]+$/.test(url)) return { code: url, type: type };
		if (type === 'spreaker' && /--[0-9]+$/.test(url)) return { code: url.match(/--([0-9]+)$/)[1], type: type };

		return { code: '', type: type };
	}

	static translate(text, lang, callback, buttonsSelector = '[data-actbar="translate"]', onError = null, $clickedButton = null) {
		const resolved_buttons_selector = buttonsSelector ?? '[data-actbar="translate"]';
		const $activeButton = $clickedButton?.length
			? $clickedButton
			: $(resolved_buttons_selector).filter('[data-lang="' + lang + '"]').first();
		const $groupButtons = $activeButton.closest('.content-translate-buttons').find('[data-actbar="translate"]');
		const $buttonsToDisable = $groupButtons.length ? $groupButtons : $activeButton;

		const restoreTranslateButtons = () => {
			if (!$activeButton.length) {
				return;
			}

			$buttonsToDisable.prop('disabled', false);

			const originalHtml = $activeButton.data('translate-original-html');
			if (originalHtml !== undefined) {
				$activeButton.html(originalHtml);
				$activeButton.removeData('translate-original-html');
			}
			$activeButton.removeClass('is-translating');
		};

		if ($activeButton.length) {
			$buttonsToDisable.prop('disabled', true);
			$activeButton.data('translate-original-html', $activeButton.html());
			$activeButton
				.addClass('is-translating')
				.html('<i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>');
		}

		$.ajax({
			type: 'POST',
			url: StringUtils.localizeUrl(window.appconfig.routes.contents.translate),
			data: {
				data: JSON.stringify(
					Array.isArray(text)
						? text.map((t) => ({
								text: t.text.trim(),
								index: t.index,
							}))
						: [{ text: text.trim(), index: 0 }],
				),
				lang: lang,
			},
			complete: restoreTranslateButtons,
			success: function (data) {
				if (data?.error) {
					// When the caller provides onError it owns the error UI, so skip the toast.
					if (!onError) window.toastr?.warning?.(data.error);
					onError?.(data.error, data.code ?? null);

					return;
				}

				callback(data);
				// $btnIcon.removeAttr('class').addClass('icon fa-regular fa-flag');
				$(resolved_buttons_selector).removeClass('btn-primary').addClass('btn-default');
				$(resolved_buttons_selector + '[data-lang="' + lang + '"]')
					.removeClass('btn-default')
					.addClass('btn-primary');
				toastr.success('Traduzione Eseguita con successo');
			},
			fail: function () {
				const message = 'Si è verificato un errore inatteso, contattare il dipartimento IT';
				// When the caller provides onError it owns the error UI, so skip the toast.
				if (!onError) toastr.error(message);
				onError?.(message, null);
				// $btnIcon.removeAttr('class').addClass('icon fa-regular fa-flag');
			},
			error: function (xhr) {
				const message =
					xhr.responseJSON?.error ?? 'Si è verificato un errore inatteso, contattare il dipartimento IT';
				const code = xhr.responseJSON?.code ?? null;

				// When the caller provides onError it owns the error UI, so skip the toast.
				if (xhr.status === 422) {
					if (!onError) window.toastr?.warning?.(message);
					onError?.(message, code);

					return;
				}

				if (!onError) toastr.error(message);
				onError?.(message, code);
				// $btnIcon.removeAttr('class').addClass('icon fa-regular fa-flag');
			},
		});
	}

	static cleanText(text) {
		if (!text || typeof text !== 'string') return '';
		return text
			.replace(/<[^>]*>/g, '') // Rimuovi HTML tags
			.replace(/&[^;]+;/g, ' ') // Rimuovi entità HTML
			.replace(/[^\w\s]/g, ' ') // Rimuovi punteggiatura
			.replace(/\s+/g, ' ') // Normalizza spazi
			.trim();
	}

	static getReadableTextDuration(seconds) {
		if (seconds > 60) return `${Math.ceil(seconds / 60)}m`;
		return `${seconds}s`;
	}

	static countWords(text) {
		return this.cleanText(text).split(/\s+/).length;
	}

	static countNoSpacesChars(text) {
		return this.cleanText(text).replace(/ /g, '').length;
	}

	/**
	 * Calcola il tempo di lettura per un oggetto EditorJS
	 * @param {Object} editorData - Oggetto EditorJS con blocks
	 * @returns {number} Tempo di lettura in secondi
	 */
	static calculateReadingTime(editorBlocks) {
		if (!editorBlocks || !Array.isArray(editorBlocks) || editorBlocks.length === 0) {
			return 0;
		}

		// Configurazione velocità di lettura (parole al minuto)
		const READING_SPEEDS = {
			base: 225, // Velocità base
			technical: 180, // Contenuto tecnico
			simple: 250, // Contenuto semplice
			code: 150, // Codice
			list: 200, // Liste
			quote: 200, // Citazioni
		};

		// Fattori di complessità per tipo di blocco
		const BLOCK_COMPLEXITY = {
			paragraph: 1.0,
			header: 0.8, // I titoli si leggono più velocemente
			list: 0.9, // Le liste sono più facili da scorrere
			quote: 1.1, // Le citazioni richiedono più attenzione
			code: 1.3, // Il codice richiede più tempo
			image: 1.2, // Le immagini rallentano la lettura
			video: 1.5, // I video richiedono tempo per essere processati
			audio: 1.4, // L'audio richiede tempo per essere ascoltato
			table: 1.2, // Le tabelle richiedono più tempo
			embed: 1.3, // Gli embed richiedono tempo per essere caricati
			gallery: 1.4, // Le gallerie richiedono tempo per essere visualizzate
			warning: 1.1, // I warning richiedono attenzione
			alert: 1.1, // Gli alert richiedono attenzione
			checklist: 0.9, // Le checklist sono più facili da scorrere
			delimiter: 0.1, // I delimitatori non richiedono tempo di lettura
			raw: 1.2, // Il contenuto raw richiede più tempo
		};

		let totalWords = 0;
		let totalComplexity = 0;
		let blockCount = 0;
		let hasImages = false;
		let hasVideos = false;
		let hasCode = false;
		let hasTables = false;

		// Analizza ogni blocco
		editorBlocks.forEach((block) => {
			const blockType = block.type;
			const complexity = BLOCK_COMPLEXITY[blockType] || 1.0;

			blockCount++;
			totalComplexity += complexity;

			// Rileva tipi di contenuto speciali
			if (blockType === 'image' || blockType === 'gallery') hasImages = true;
			if (blockType === 'video' || blockType === 'embed') hasVideos = true;
			if (blockType === 'code' || blockType === 'raw') hasCode = true;
			if (blockType === 'table') hasTables = true;

			// Calcola parole per tipo di blocco
			let blockWords = 0;

			switch (blockType) {
				case 'image':
				case 'gallery':
				case 'video':
				case 'audio':
				case 'embed':
				case 'delimiter':
					// Multimedia rallentano la lettura ma non aggiungono parole
					blockWords = 0;
					break;
				case 'raw':
					blockWords = this.countWords(block.data.html || '');
					break;
				case 'table':
					if (block.data.content && Array.isArray(block.data.content)) {
						blockWords = block.data.content.reduce((total, row) => {
							if (Array.isArray(row)) {
								return (
									total +
									row.reduce((rowTotal, cell) => {
										return rowTotal + this.countWords(cell || '');
									}, 0)
								);
							}
							return total;
						}, 0);
					}
					break;
				default:
					if (block.data.items && Array.isArray(block.data.items)) {
						blockWords = block.data.items.reduce((total, item) => {
							return total + this.countWords(typeof item === 'string' ? item : item.text || '');
						}, 0);
					} else if (block.data.text) {
						blockWords = this.countWords(block.data.text);
					}
					break;
			}

			totalWords += blockWords;
		});

		// Determina la velocità di lettura in base al contenuto
		let readingSpeed = READING_SPEEDS.base;

		if (hasCode) {
			readingSpeed = READING_SPEEDS.code;
		} else if (hasTables) {
			readingSpeed = READING_SPEEDS.base * 0.9; // Le tabelle rallentano
		} else if (totalWords < 100) {
			readingSpeed = READING_SPEEDS.simple; // Contenuto breve
		} else if (totalWords > 1000) {
			readingSpeed = READING_SPEEDS.technical; // Contenuto lungo
		}

		// Calcola il tempo base
		let baseTime = (totalWords / readingSpeed) * 60; // Converti in secondi

		// Applica fattori di complessità
		const avgComplexity = totalComplexity / blockCount;
		baseTime *= avgComplexity;

		// Fattori aggiuntivi per contenuti speciali
		if (hasImages) baseTime *= 1.15; // +15% per le immagini
		if (hasVideos) baseTime *= 1.25; // +25% per i video
		if (hasTables) baseTime *= 1.1; // +10% per le tabelle

		// Tempo minimo di 30 secondi per contenuti molto brevi
		// const minTime = 30;

		// Tempo massimo ragionevole (30 minuti)
		// const maxTime = 1800;

		// return Math.max(minTime, Math.min(maxTime, Math.round(baseTime)));
		return Math.round(baseTime);
	}

	/**
	 * Locale for CMS navigation: explicit `locale` query param, then default.
	 * Mirrors Helper::resolveNavigationLocale() on the server.
	 */
	static resolveNavigationLocale() {
		const current_url = new URL(location.href);
		const locale = current_url.searchParams.get('locale') ?? current_url.searchParams.get('language');

		if (typeof locale === 'string' && locale.trim() !== '') {
			return locale.trim().toLowerCase().substring(0, 2);
		}

		return window.default_locale ?? 'it';
	}

	static localizeUrl(url, locale = null) {
		if (!url) return null;

		if (!locale) {
			locale = StringUtils.resolveNavigationLocale();
		}

		if (url.includes('locale=')) {
			return url.replace(/locale\=\w{2}/, 'locale=' + locale);
		}

		return url + (url.includes('?') ? '&' : '?') + 'locale=' + locale;
	}

	/**
	 * Truncate a string to at most maxLength characters (UTF-16 code units, same as HTML maxlength).
	 *
	 * @param {string|null|undefined} value
	 * @param {number} maxLength
	 * @returns {string}
	 */
	static truncateToLength(value, maxLength) {
		const str = value == null ? '' : String(value);

		if (!Number.isFinite(maxLength) || maxLength < 0 || str.length <= maxLength) {
			return str;
		}

		return str.substring(0, maxLength);
	}

	/**
	 * Set an input value, truncating to its maxlength attribute when present.
	 * Triggers `input` so SEO counters and other listeners stay in sync.
	 * jQuery `.val()` bypasses the native maxlength attribute, so callers that
	 * auto-fill fields (e.g. IPTC caption → cover_alt) must use this helper.
	 *
	 * @param {string|HTMLElement|JQuery} input
	 * @param {string|null|undefined} value
	 * @returns {string} The value actually written
	 */
	static setInputValue(input, value) {
		const $el = $(input);

		if (!$el.length) {
			return value == null ? '' : String(value);
		}

		const maxAttr = $el.attr('maxlength');
		const maxLength = maxAttr !== undefined && maxAttr !== '' ? parseInt(maxAttr, 10) : NaN;
		const truncated = StringUtils.truncateToLength(value, maxLength);

		$el.val(truncated).trigger('input');

		return truncated;
	}
}
